import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { axiosInstance } from '@/lib/axios';

export interface FeatureFlags {
  bookingPlatform:       boolean;
  customAdventures:      boolean;
  businessAnalytics:     boolean;
  businessBookings:      boolean;
  businessCalendar:      boolean;
  businessCommunication: boolean;
  businessStaff:         boolean;
  businessMapping:       boolean;
  businessLivestock:     boolean;
  businessFunding:       boolean;
  partnerStats:          boolean;
}

// Mirrors the backend DEFAULT_FLAGS — used as fallback if fetch fails
const FALLBACK: FeatureFlags = {
  bookingPlatform:       false,
  customAdventures:      false,
  businessAnalytics:     false,
  businessBookings:      false,
  businessCalendar:      false,
  businessCommunication: false,
  businessStaff:         false,
  businessMapping:       false,
  businessLivestock:     false,
  businessFunding:       false,
  partnerStats:          true,
};

interface FeaturesContextValue {
  flags: FeatureFlags;
  loading: boolean;
  reload: () => void;
}

const FeaturesContext = createContext<FeaturesContextValue>({
  flags: FALLBACK,
  loading: true,
  reload: () => {},
});

export function FeaturesProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags]   = useState<FeatureFlags>(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [tick, setTick]     = useState(0);

  useEffect(() => {
    axiosInstance.get('/features')
      .then(r => setFlags({ ...FALLBACK, ...r.data.flags }))
      .catch(() => setFlags(FALLBACK))       // fail-safe: use defaults, never crash
      .finally(() => setLoading(false));
  }, [tick]);

  const reload = () => setTick(t => t + 1);

  return (
    <FeaturesContext.Provider value={{ flags, loading, reload }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures(): FeatureFlags {
  return useContext(FeaturesContext).flags;
}

export function useFeaturesContext() {
  return useContext(FeaturesContext);
}
